import React, { useState } from 'react';
import {
  Box, Typography, Checkbox, IconButton
} from '@mui/material';
import {
  FolderOpen as FolderIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { folderUtils } from '../utils/formBuilderUtils';

const FolderTreeView = ({ folderOptions, selectedFolders, setSelectedFolders }) => {
  const { t } = useTranslation();
  // Açık klasörleri takip etmek için state
  const [expandedFolders, setExpandedFolders] = useState({});
  
  // Klasörü aç/kapat
  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };
  
  // Checkbox değişimi
  const handleFolderSelect = (folderId) => {
    setSelectedFolders(prev => {
      if (prev.includes(folderId)) {
        return prev.filter(id => id !== folderId);
      } else {
        return [...prev, folderId];
      }
    });
  };
  
  // Recursive olarak klasör yapısını render et
  const renderFolderItem = (folder) => {
    const hasSubfolders = folderUtils.hasChildren(folder.value, folderOptions);
    const isExpanded = expandedFolders[folder.value];
    const isSelected = selectedFolders.includes(folder.value);
    const children = folderUtils.getChildren(folder.value, folderOptions);
    
    return (
      <Box key={folder.value} sx={{ mb: 1 }}>
        <Box 
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover'
            },
            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
            border: isSelected ? '1px solid' : '1px solid transparent',
            borderColor: isSelected ? 'primary.main' : 'transparent',
          }}
        >
          {hasSubfolders ? (
            <IconButton 
              size="small" 
              onClick={() => toggleFolder(folder.value)}
              sx={{ mr: 0.5 }}
            >
              {isExpanded ? (
                <Box sx={{ color: 'text.secondary', fontSize: 18 }}>▼</Box>
              ) : (
                <Box sx={{ color: 'text.secondary', fontSize: 18 }}>▶</Box>
              )}
            </IconButton>
          ) : (
            <Box sx={{ width: 28 }} /> // Boşluk tutucu
          )}
          
          <Checkbox 
            checked={isSelected}
            onChange={() => handleFolderSelect(folder.value)}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          
          <FolderIcon 
            sx={{ 
              ml: 0.5, 
              mr: 1.5, 
              color: isSelected ? 'primary.main' : 'action.active' 
            }} 
          />
          
          <Typography 
            variant="body2"
            sx={{ 
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? 'primary.main' : 'text.primary'
            }}
            onClick={() => handleFolderSelect(folder.value)}
          >
            {folder.label}
          </Typography>
        </Box>
        
        {/* Alt klasörleri render et (açıksa) */}
        {isExpanded && hasSubfolders && (
          <Box sx={{ pl: 4 }}>
            {children.map(childFolder => renderFolderItem(childFolder))}
          </Box>
        )}
      </Box>
    );
  };
  
  // Ana klasörleri bul
  const rootFolders = folderUtils.buildFolderTree(folderOptions);
  
  return (
    <Box 
      sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        maxHeight: '400px',
        overflow: 'auto'
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {t('folder_structure')}
      </Typography>
      {rootFolders.map(folder => renderFolderItem(folder))}
    </Box>
  );
};

export default FolderTreeView;
